import React from 'react';
import { Avatar, Dialog, DialogContent, DialogHeader, DialogTitle } from '@parsina/framework';
import { useTranslation } from '../i18n/I18nProvider.jsx';

/**
 * A patient's avatar that opens the photo full-size when there is one to open.
 *
 * CLICKABLE ONLY WHEN A PHOTO EXISTS. Initials are generated from the name, so
 * there is nothing behind them to enlarge — wrapping them in a button would put
 * a focus stop on every row of the day sheet and the register that does nothing
 * when activated. `src` is what decides, not the patient.
 *
 * The stored image is 512×512 (the storage service downscales on upload), so the
 * dialog caps at that: scaling past the source only produces a blurrier picture
 * than the one already on screen.
 */
const PatientAvatar = ({ name, src, size = 40, bg, alt }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const avatar = <Avatar name={name} src={src || undefined} size={size} bg={bg} />;
  if (!src) return avatar;

  const label = alt || name || '';

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Every one of these avatars sits inside something else that is
          // clickable — a day-sheet row, a register row, a page header with a
          // back affordance. Without this, opening the photo also navigates,
          // and the dialog appears over a screen the dentist did not ask for.
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={t('patient.photo.open', { name: label })}
        style={{
          padding: 0, border: 'none', background: 'none', cursor: 'zoom-in',
          borderRadius: '50%', display: 'block', flexShrink: 0, lineHeight: 0,
        }}
      >
        {avatar}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          <img
            src={src}
            alt={t('patient.photo.alt', { name: label })}
            style={{
              display: 'block', width: '100%', maxWidth: 512, height: 'auto',
              margin: '0 auto', borderRadius: 12,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientAvatar;
